import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { Project, ProjectStatus, ProjectFormData } from '../../types/Project';
import { projectService } from '../../services/projectService';
import ProjectList from './ProjectList';
import PageHeader from '../common/PageHeader';
import './ProjectManagement.css';
import './ProjectList.css';

interface ProjectManagementProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const ProjectManagement: React.FC<ProjectManagementProps> = ({ user, onToggleSidebar }) => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Load projects
  useEffect(() => {
    if (user) {
      loadProjects();
    }
  }, [user]);

  // Handle clicks outside menu to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showMenu && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const loadProjects = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const result = await projectService.getUserProjects({
        user_id: user.id.toString()
      });
      
      if (result.success && result.projects) {
        const convertedProjects = result.projects.map(project => projectService.convertDatabaseProject(project));
        setProjects(convertedProjects);
      } else {
        setProjects([]);
      }
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load projects');
      console.error('Error loading projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProject = () => {
    navigate('/projects/new');
  };

  const handleProjectClick = (project: Project) => {
    navigate(`/projects/${project.id}`);
  };

  const handleDeleteProject = async (projectId: string) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        setLoading(true);
        const result = await projectService.deleteProject(projectId);
        
        if (result.success) {
          // Remove project from local state
          setProjects(prev => prev.filter(p => p.id !== projectId));
        } else {
          throw new Error('Failed to delete project');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to delete project');
        console.error('Error deleting project:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  if (!user) {
    return (
      <div className="project-management">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="project-management-header-title">Projects</h1>}
        />
        <div className="project-management-auth-required">
          <p>Please sign in to manage your projects.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="project-management">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="project-management-header-title">Projects</h1>}
        rightActions={
          <div className="project-management-menu-container" ref={menuRef}>
            <button
              className="project-management-menu-btn"
              onClick={() => setShowMenu(!showMenu)}
              title="Menu"
            >
              ⋮
            </button>
            {showMenu && (
              <div className="project-management-menu-dropdown">
                <button
                  className="project-management-menu-item"
                  onClick={() => {
                    handleAddProject();
                    setShowMenu(false);
                  }}
                >
                  + Add Project
                </button>
              </div>
            )}
          </div>
        }
      />

      {error && (
        <div className="project-management-error-banner">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="project-management-content">
        <ProjectList
          projects={projects}
          loading={loading}
          onProjectClick={handleProjectClick}
          onAddProject={handleAddProject}
          showAddButton={true}
          emptyStateTitle="No projects yet"
          emptyStateMessage="Start by creating your first project to track your progress!"
          emptyStateButtonText="Create Your First Project"
        />
      </div>
    </div>
  );
};

export default ProjectManagement;

